import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "../components/paymentproof.css";
import api, { API_ENDPOINTS } from "../config";

const PaymentProof = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const name = searchParams.get("name");

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!["image/jpeg", "image/png", "application/pdf"].includes(selectedFile.type)) {
      setMessage("Only JPG, PNG, or PDF files are allowed.");
      return;
    }

    if (selectedFile.size > 10 * 1024 * 1024) {
      setMessage("File is too large. Max size is 10MB.");
      return;
    }

    setFile(selectedFile);
    setPreview(selectedFile.type.startsWith("image/") ? URL.createObjectURL(selectedFile) : null);
    setMessage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return setMessage("Please select a file first.");
    if (!token) return setMessage("Invalid upload link.");

    const formData = new FormData();
    formData.append("receipt", file);
    if (name) formData.append("name", name);

    try {
      setLoading(true);
      setMessage("");

      await api.post(
        `${API_ENDPOINTS.ACCEPT_RECEIPT}/${token}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      setMessage("✅ Proof submitted successfully. We will verify shortly.");
      setFile(null);
      setPreview(null);
    } catch (error) {
      console.error("Upload error:", error.response || error);

      if (error.response?.status === 404) {
        setMessage("Invalid or expired upload link.");
      } else if (error.response?.status === 400) {
        setMessage("Invalid file upload.");
      } else if (error.response?.status === 413) {
        setMessage("File too large. Please upload under 10MB.");
      } else if (error.response?.status === 409) {
        setMessage("⚠️ This link has already been used. Upload expired.");
      } else {
        setMessage("⚠️ Error submitting proof. Try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="proof-container">
      {/* Helmet added here */}
      <Helmet>
        <title>Upload your payment proof</title>
        <meta
          name="description"
          content="Upload your payment proof securely to Iklim Digitals. Supported formats: JPG, PNG, PDF. Max file size 10MB."
        />
        <meta
          name="keywords"
          content="Iklim Digitals, payment proof, upload receipt, secure file upload, Ethiopia digital services"
        />
      </Helmet>

      <h2 className="proof-title">Payment Proof Upload</h2>

      {name && (
        <p className="proof-label">
          Name: <span className="proof-name">{name}</span>
        </p>
      )}

      <p className="proof-info">
        Please upload a clear screenshot/photo of your payment transaction.
        <br />Maximum file size: 10MB. Supported: JPG, PNG, PDF.
      </p>

      <form onSubmit={handleSubmit} className="proof-form">
        <input
          type="file"
          accept="image/png, image/jpeg, application/pdf"
          onChange={handleFileChange}
          className="proof-input"
        />

        {preview && (
          <div className="proof-preview">
            <p className="proof-preview-label">Preview:</p>
            <img src={preview} alt="Preview" className="proof-img" />
          </div>
        )}

        {file?.type === "application/pdf" && (
          <div className="proof-preview">
            <p className="proof-preview-label">PDF Preview:</p>
            <a
              href={URL.createObjectURL(file)}
              target="_blank"
              rel="noopener noreferrer"
              className="proof-pdf-link"
            >
              Open PDF
            </a>
          </div>
        )}

        <button type="submit" disabled={loading} className="proof-btn">
          {loading ? "Uploading..." : "Submit Proof"}
        </button>
      </form>

      {message && <p className="proof-message">{message}</p>}
    </div>
  );
};

export default PaymentProof;
